import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Owns the mini-map drag / expand / collapse animation state for the
 * Guest Map. Extracted so `GuestMapPage` doesn't have to juggle the
 * mapHeight state, drag refs, pointer handlers, and the rAF resize
 * loop alongside everything else.
 *
 * CRITICAL — the rAF resize loop inside `animateMapResize` fixes the
 * "map goes white on expand" bug. The maplibre canvas is only sized
 * via `map.resize()`; without this loop, the container grows each CSS
 * transition frame while the canvas stays at the previous size,
 * exposing white space until the final resize lands.
 *
 * Params:
 *   mapRef               — ref to the maplibregl.Map instance
 *   getMaxHeight()       — returns the full-open height for this device
 *   effectiveMiniHeight  — the "mini" state height (compact vs normal)
 *   setSelectedMapVenueId — clears the map preview sheet on minimize
 *   animMs (default 260) — matches the CSS height transition duration
 *
 * Returns:
 *   mapHeight, setMapHeight, isDragging, isExpanded
 *   animateMapResize, openMapFully, minimizeMap
 *   onPointerDown, onPointerMove, onPointerUp
 */
export function useGuestMapDrag({
  mapRef,
  getMaxHeight,
  effectiveMiniHeight,
  setSelectedMapVenueId,
  animMs = 260,
}) {
  const [mapHeight, setMapHeight] = useState(effectiveMiniHeight);
  const [isDragging, setIsDragging] = useState(false);
  // Expansion is user intent, NOT a guess based on the current IME-sized height.
  const [isExpanded, setIsExpanded] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const resizeFrameRef = useRef(null);
  const hasInteractedRef = useRef(false);
  const dragStartY = useRef(0);
  const dragStartHeight = useRef(effectiveMiniHeight);

  const animateMapResize = useCallback((nextHeight) => {
    cancelAnimationFrame(resizeFrameRef.current);
    setMapHeight(Math.max(0, nextHeight));
    setIsResizing(true);
    // Resize the maplibre canvas CONTINUOUSLY across the CSS height
    // transition so the canvas grows/shrinks in lockstep with the
    // container — otherwise the container reveals a blank strip and
    // the map appears to "go white" for a beat when it expands.
    // resize() is cheap: it only rescales the WebGL viewport, no tile
    // refetch.
    const start = performance.now();
    const tick = () => {
      const map = mapRef.current;
      const container = map?.getContainer?.();
      const canvas = map?.getCanvas?.();
      // resize() emits moveend even at identical dimensions. Avoid repeatedly
      // filtering markers / redrawing tiles while waiting for the CSS transition.
      if (!container || !canvas || container.clientHeight !== canvas.clientHeight
        || container.clientWidth !== canvas.clientWidth) map?.resize();
      if (performance.now() - start < animMs + 40) {
        resizeFrameRef.current = requestAnimationFrame(tick);
      } else {
        map?.resize();
        resizeFrameRef.current = null;
        setIsResizing(false);
      }
    };
    resizeFrameRef.current = requestAnimationFrame(tick);
  }, [mapRef, animMs]);

  const openMapFully = useCallback(() => {
    hasInteractedRef.current = true;
    setIsExpanded(true);
    animateMapResize(getMaxHeight());
  }, [animateMapResize, getMaxHeight]);

  const minimizeMap = useCallback(() => {
    hasInteractedRef.current = true;
    setIsExpanded(false);
    setSelectedMapVenueId(null);
    animateMapResize(effectiveMiniHeight);
  }, [animateMapResize, effectiveMiniHeight, setSelectedMapVenueId]);

  // Android adjustResize changes innerHeight during keyboard dismissal. Keep
  // following the actual window until restored, including tag-row wrapping.
  // A short intermediate height must never erase the user's expansion intent.
  useEffect(() => {
    if (isDragging) return undefined;
    const resize = () => animateMapResize(isExpanded ? getMaxHeight() : effectiveMiniHeight);
    resize();
    window.addEventListener('resize', resize);
    window.visualViewport?.addEventListener('resize', resize);
    return () => {
      window.removeEventListener('resize', resize);
      window.visualViewport?.removeEventListener('resize', resize);
    };
  }, [isExpanded, isDragging, getMaxHeight, effectiveMiniHeight, animateMapResize]);

  useEffect(() => () => cancelAnimationFrame(resizeFrameRef.current), []);

  const handleDragStart = (clientY) => {
    if (isExpanded) return;
    hasInteractedRef.current = true;
    setIsDragging(true);
    dragStartY.current = clientY;
    dragStartHeight.current = mapHeight;
  };

  const handleDragMove = (clientY) => {
    if (!isDragging) return;
    const delta = dragStartY.current - clientY; // positive = dragging up
    if (dragStartHeight.current === effectiveMiniHeight && delta > 42) {
      setIsDragging(false);
      openMapFully();
      return;
    }
    const newHeight = Math.max(
      effectiveMiniHeight,
      Math.min(getMaxHeight(), dragStartHeight.current + delta),
    );
    setMapHeight(newHeight);
  };

  const handleDragEnd = () => {
    if (!isDragging) return;
    setIsDragging(false);
    const maxH = getMaxHeight();
    const draggedUp = mapHeight - effectiveMiniHeight;
    const expanded = draggedUp > 34;
    setIsExpanded(expanded);
    const targetHeight = expanded ? maxH : effectiveMiniHeight;
    animateMapResize(targetHeight);
  };

  const onPointerDown = (event) => {
    if (isExpanded) return;
    event.currentTarget.setPointerCapture?.(event.pointerId);
    handleDragStart(event.clientY);
  };

  const onPointerMove = (event) => {
    if (!isDragging) return;
    event.preventDefault();
    handleDragMove(event.clientY);
  };

  const onPointerUp = (event) => {
    if (event.currentTarget.hasPointerCapture?.(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    handleDragEnd();
  };

  return {
    mapHeight,
    setMapHeight,
    isDragging,
    isExpanded,
    isResizing,
    hasInteractedRef,
    animateMapResize,
    openMapFully,
    minimizeMap,
    onPointerDown,
    onPointerMove,
    onPointerUp,
  };
}
